/*
  East Side Studio - corner photo artwork renderer (v7c: smooth sampling + minimum print scale).
  Draws an artwork into a frame's corner photo using the placement data stored in
  product metafield custom.corner_photo_placement.

  renderCornerArtwork(canvas, photo, art, placement, printWidthMm, printHeightMm)
    canvas     : <canvas> to draw into (its size is set to the photo's natural size)
    photo      : loaded <img> of the corner photo (any CDN size; coordinates are scaled)
    art        : loaded <img>/<canvas> of the artwork, or null to draw the plain photo
    placement  : parsed JSON from custom.corner_photo_placement
    printWidthMm, printHeightMm : the print size (portrait numbers; swapped automatically
                 for landscape artwork), e.g. 297, 420 for A3

  How it works:
  - The artwork is cover-fitted to the print's proportions and shown from its bottom-left
    corner (the photo shows the frame's bottom-left corner, so the artwork reads upright).
  - Its outer placement.overlap_mm (5 mm) sits under the frame's rebate, as in real framing.
  - Minimum scale: the photo shows the corner of a large frame, so a small print would end
    inside the visible paper. minPrintMm() works out (once per photo) the smallest print that
    fills all the visible paper, plus 3%; smaller sizes are drawn at that size, keeping their
    proportions. From that size up, prints are drawn at true scale.
  - It is drawn through an affine transform with the browser's own image smoothing, clipped to
    the wedge between the frame's inner face edge (camera-facing rail) and the paper edge (other
    rail), and MULTIPLIED by the photo (brightened by 255 / paper brightness), so the photo's own
    light and shadow fall on it.
*/
(function (global) {
  function orient(p) {
    var up = p.dir_up, dn = p.dir_down, fd = p.face_line_dir;
    if (fd[0] * up[0] + fd[1] * up[1] < 0) fd = [-fd[0], -fd[1]];
    var nU = [-up[1], up[0]]; if (nU[0] * dn[0] + nU[1] * dn[1] < 0) nU = [-nU[0], -nU[1]];
    var fdn = [-fd[1], fd[0]]; if (fdn[0] * nU[0] + fdn[1] * nU[1] < 0) fdn = [-fdn[0], -fdn[1]];
    var nD = [-dn[1], dn[0]]; if (nD[0] * up[0] + nD[1] * up[1] < 0) nD = [-nD[0], -nD[1]];
    return { up: up, dn: dn, fd: fd, fdn: fdn, nD: nD };
  }

  // Smallest print [mm along the bottom rail, mm up the side rail] that fills the visible paper.
  function minPrintMm(p) {
    if (p.__minMm) return p.__minMm;
    var o = orient(p), W = p.image.width, H = p.image.height, C = p.corner, fc = p.face_line_point;
    var k = p.px_per_mm, ovl = p.overlap_mm, det = o.dn[0] * o.up[1] - o.up[0] * o.dn[1];
    var maxA = 0, maxB = 0, step = 8;
    for (var y = 0; y <= H; y += step) {
      for (var x = 0; x <= W; x += step) {
        if ((x - fc[0]) * o.fdn[0] + (y - fc[1]) * o.fdn[1] <= 0) continue;          // outside the face edge
        if ((x - C[0]) * o.nD[0] + (y - C[1]) * o.nD[1] <= -0.5) continue;           // below the paper edge
        var dx = x - C[0], dy = y - C[1];
        var a = (dx * o.up[1] - o.up[0] * dy) / (k * det) + ovl, b = (o.dn[0] * dy - o.dn[1] * dx) / (k * det) + ovl;
        if (a > maxA) maxA = a; if (b > maxB) maxB = b;
      }
    }
    p.__minMm = [maxA * 1.03, maxB * 1.03];
    return p.__minMm;
  }

  function renderCornerArtwork(canvas, photo, art, placement, printWidthMm, printHeightMm) {
    var W = photo.naturalWidth || photo.width, H = photo.naturalHeight || photo.height;
    var s = W / placement.image.width;                       // coordinate scale for resized photos
    canvas.width = W; canvas.height = H;
    var ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalCompositeOperation = 'source-over'; ctx.filter = 'none';
    ctx.drawImage(photo, 0, 0, W, H);
    if (!art) return;

    var aw = art.naturalWidth || art.width, ah = art.naturalHeight || art.height;
    var land = aw > ah;
    var wmm = land ? printHeightMm : printWidthMm, hmm = land ? printWidthMm : printHeightMm;
    var need = minPrintMm(placement), f = Math.max(1, need[0] / wmm, need[1] / hmm);   // minimum scale
    wmm *= f; hmm *= f;
    var ar = aw / ah, pr = wmm / hmm, cw = aw, ch = ah, cx0 = 0, cy0 = 0;   // cover-fit crop
    if (ar > pr) { cw = ah * pr; cx0 = (aw - cw) / 2; } else { ch = aw / pr; cy0 = (ah - ch) / 2; }
    var sx = cw / wmm, sy = ch / hmm;
    var o = orient(placement), up = o.up, dn = o.dn, fd = o.fd;
    var k = placement.px_per_mm * s, ovl = placement.overlap_mm;
    var C = [placement.corner[0] * s, placement.corner[1] * s];
    var fc = [placement.face_line_point[0] * s, placement.face_line_point[1] * s];

    // visible wedge: apex Q = face line meets the paper's edge line on the other rail
    var den = dn[0] * fd[1] - dn[1] * fd[0];
    var t = ((fc[0] - C[0]) * fd[1] - (fc[1] - C[1]) * fd[0]) / den;
    var Q = [C[0] + dn[0] * t, C[1] + dn[1] * t], L = 8000 * s;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(Q[0], Q[1]);
    ctx.lineTo(Q[0] + fd[0] * L, Q[1] + fd[1] * L);
    ctx.lineTo(Q[0] + (fd[0] + dn[0]) * L, Q[1] + (fd[1] + dn[1]) * L);
    ctx.lineTo(Q[0] + dn[0] * L, Q[1] + dn[1] * L);
    ctx.closePath();
    ctx.clip();

    ctx.globalCompositeOperation = 'multiply';
    ctx.filter = 'brightness(' + (255 / placement.paper_median_brightness).toFixed(4) + ')';
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    // artwork pixel (u, v) -> photo: X = C + k*dn*((u-cx0)/sx - ovl) + k*up*((cy0+ch-v)/sy - ovl)
    var ex = C[0] + k * dn[0] * (-cx0 / sx - ovl) + k * up[0] * ((cy0 + ch) / sy - ovl);
    var ey = C[1] + k * dn[1] * (-cx0 / sx - ovl) + k * up[1] * ((cy0 + ch) / sy - ovl);
    ctx.setTransform(k * dn[0] / sx, k * dn[1] / sx, -k * up[0] / sy, -k * up[1] / sy, ex, ey);
    ctx.drawImage(art, cx0, cy0, cw, ch, cx0, cy0, cw, ch);
    ctx.restore();
  }

  // Parse a size option such as '30cm x 40cm', 'A3 - 29.7cm x 42.0cm' or '12\" x 16\"' into [w, h] mm.
  // ISO paper sizes for labels written without measurements (\"A3\", \"A3 Plus\"), as on some frame products.
  var ISO = { a0: [841, 1189], a1: [594, 841], a2: [420, 594], a3: [297, 420], a4: [210, 297], a5: [148, 210], a6: [105, 148], a3plus: [329, 483] };
  function parseSizeMm(label) {
    var m = String(label).match(/([\d.]+)\s*(cm|mm|\")?\s*x\s*([\d.]+)\s*(cm|mm|\")/i);
    if (!m) {
      var iso = String(label).trim().match(/^A([0-6])(\s*\+|\s*plus)?$/i);
      return iso ? ISO['a' + iso[1] + (iso[2] ? 'plus' : '')] || null : null;
    }
    var unit = (m[4] || m[2] || 'cm').toLowerCase();
    var f = unit === 'mm' ? 1 : unit === '\"' ? 25.4 : 10;
    var a = parseFloat(m[1]) * f, b = parseFloat(m[3]) * f;
    return [Math.min(a, b), Math.max(a, b)];
  }

  global.ESSCornerArtwork = { render: renderCornerArtwork, parseSizeMm: parseSizeMm, minPrintMm: minPrintMm };
})(window);
